import { dist, polygonArea, polylineLength, type Pt } from '../model/geom';
import { formatLength, normalizeAngle, radToDeg, toSquareMeters } from '../model/units';
import type { CadEntity } from '../model/entity';
import type { Command, CmdContext, CommandRunner } from './types';

/**
 * 조회 명령 (작업지시서 P2-17)
 *   DIST · AREA · LIST
 *
 * ★ 마트 실무에서 가장 많이 쓰이는 기능이다.
 *   "이 통로 폭이 2.4m 나오나?" 를 그 자리에서 재는 것.
 *   도면을 바꾸지 않으므로 열람 권한만 있어도 실행할 수 있다(readOnly).
 */

/* ------------------------------------------------------------------ DIST */

export const DIST: Command = {
  name: 'DIST',
  aliases: ['DI', 'MEASURE'],
  label: '거리 재기',
  hint: '두 점 사이 거리를 잽니다',
  readOnly: true,
  *run(ctx: CmdContext): CommandRunner {
    const unit = ctx.scene.doc.displayUnit;

    const a = yield { type: 'point', message: '첫 번째 점 지정' };
    if (a.t !== 'point') return;

    const b = yield {
      type: 'point',
      message: '두 번째 점 지정',
      base: a.p,
      preview: (cursor) => [
        {
          id: 'preview',
          layer: 'A-DIM',
          type: 'line',
          a: a.p,
          b: cursor,
        } as CadEntity,
      ],
    };
    if (b.t !== 'point') return;

    const d = dist(a.p, b.p);
    const dx = b.p.x - a.p.x;
    const dy = b.p.y - a.p.y;
    const angleDeg = radToDeg(normalizeAngle(Math.atan2(dy, dx)));

    ctx.log(
      `거리 = ${formatLength(Math.round(d), unit)} ${unit}` +
        `  ·  ΔX = ${formatLength(dx, unit)}` +
        `  ·  ΔY = ${formatLength(dy, unit)}` +
        `  ·  각도 = ${angleDeg.toFixed(1)}°`
    );
    return;
  },
};

/* ------------------------------------------------------------------ AREA */

export const AREA: Command = {
  name: 'AREA',
  aliases: ['AA'],
  label: '면적 재기',
  hint: '여러 점을 찍어 면적을 잽니다',
  readOnly: true,
  *run(ctx: CmdContext): CommandRunner {
    const unit = ctx.scene.doc.displayUnit;
    const points: Pt[] = [];

    const first = yield { type: 'point', message: '첫 번째 점 지정' };
    if (first.t !== 'point') return;
    points.push(first.p);

    for (;;) {
      const snapshot = [...points];
      const token = yield {
        type: 'point',
        message: '다음 점 지정 (계산은 Enter)',
        base: points[points.length - 1]!,
        allowEnter: true,
        preview: (cursor) => [
          {
            id: 'preview',
            layer: 'A-DIM',
            type: 'polyline',
            points: [...snapshot, cursor],
            closed: true,
          } as CadEntity,
        ],
      };
      if (token.t === 'enter' || token.t === 'esc') break;
      if (token.t === 'point') points.push(token.p);
    }

    if (points.length < 3) {
      ctx.log('면적을 계산하려면 점이 3개 이상 필요합니다.');
      return;
    }

    const area = polygonArea(points);
    const perimeter = polylineLength(points, true);

    ctx.log(
      `면적 = ${toSquareMeters(area).toFixed(2)} ㎡` +
        `  ·  둘레 = ${formatLength(Math.round(perimeter), unit)} ${unit}`
    );
    return;
  },
};

/* ------------------------------------------------------------------ LIST */

export const LIST: Command = {
  name: 'LIST',
  aliases: ['LI'],
  label: '객체 정보',
  hint: '선택한 객체의 상세 정보를 봅니다',
  readOnly: true,
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '정보를 볼 객체 선택', useExisting: true };
    if (sel.t !== 'selection' || sel.ids.length === 0) return;

    const unit = ctx.scene.doc.displayUnit;

    for (const id of sel.ids.slice(0, 20)) {
      const e = ctx.scene.get(id);
      if (!e) continue;
      ctx.log(describeEntity(e, unit));
    }
    if (sel.ids.length > 20) ctx.log(`… 외 ${sel.ids.length - 20}개`);
    return;
  },
};

function describeEntity(e: CadEntity, unit: 'mm' | 'cm' | 'm'): string {
  const head = `[${e.layer}] `;
  switch (e.type) {
    case 'line':
      return `${head}선 · 길이 ${formatLength(Math.round(dist(e.a, e.b)), unit)} ${unit}`;
    case 'polyline': {
      const l = polylineLength(e.points, e.closed);
      const extra = e.closed
        ? ` · 면적 ${toSquareMeters(polygonArea(e.points)).toFixed(2)} ㎡`
        : '';
      return `${head}폴리선 · 정점 ${e.points.length}개 · 길이 ${formatLength(Math.round(l), unit)} ${unit}${extra}`;
    }
    case 'circle':
      return `${head}원 · 반지름 ${formatLength(e.radius, unit)} ${unit}`;
    case 'arc':
      return `${head}호 · 반지름 ${formatLength(e.radius, unit)} ${unit}`;
    case 'text':
      return `${head}문자 · "${e.text}"`;
    case 'blockref':
      return `${head}블록 · ${e.blockCode} · 배율 ${e.scale}`;
    case 'dimension':
      return `${head}치수 · ${e.kind}`;
  }
}

export const INQUIRY_COMMANDS: Command[] = [DIST, AREA, LIST];
